/* Added the following code for POW-938.Harsha using AI Agent. September 20, 2026. Code Start */
/**
 * Teams-style bottom-right notification popups.
 * - User must close each popup (no auto-dismiss).
 * - On login, historical / previous-day unread items are not toasted.
 * - While this browser tab is hidden, notifications are queued; on return only the latest few are shown.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { X } from 'lucide-react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useNotificationsContext } from 'app/context/NotificationsContext';

const accentBlue = '#384AAC';
const MAX_VISIBLE_WHILE_ACTIVE = 6;
const MAX_VISIBLE_AFTER_TAB_RETURN = 3;

const useStyles = makeStyles(theme => ({
	root: {
		position: 'fixed',
		right: 16,
		bottom: 24,
		zIndex: 99999998,
		display: 'flex',
		flexDirection: 'column-reverse',
		gap: theme.spacing(1),
		pointerEvents: 'none',
		maxWidth: 'min(380px, calc(100vw - 32px))',
		maxHeight: 'calc(100vh - 48px)',
		overflowY: 'auto',
		overflowX: 'hidden',
		paddingBottom: 'env(safe-area-inset-bottom, 0px)',
		boxSizing: 'border-box'
	},
	toast: {
		pointerEvents: 'auto',
		padding: theme.spacing(1.5, 1.5, 1.75),
		borderRadius: 8,
		boxSizing: 'border-box',
		overflow: 'visible',
		boxShadow:
			'rgba(0, 0, 0, 0.2) 0px 5px 5px -3px, rgba(0, 0, 0, 0.14) 0px 8px 10px 1px, rgba(0, 0, 0, 0.12) 0px 3px 14px 2px',
		borderLeft: `3px solid ${accentBlue}`,
		backgroundColor: '#fff',
		display: 'flex',
		alignItems: 'flex-start',
		gap: theme.spacing(1)
	},
	toastImportant: {
		borderLeftColor: '#d32f2f'
	},
	body: {
		flex: 1,
		minWidth: 0
	},
	title: {
		fontWeight: 600,
		color: accentBlue,
		fontSize: 14,
		lineHeight: 1.35,
		wordBreak: 'break-word',
		margin: 0,
		/* Added the following code for POW-938.Harsha using AI Agent. September 20, 2026. Code Start */
		display: '-webkit-box',
		WebkitLineClamp: 3,
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden'
		/* Code end. Harsha using AI Agent. September 20, 2026 */
	},
	titleImportant: {
		color: '#d32f2f'
	},
	preview: {
		margin: 0,
		marginTop: 4,
		fontSize: 12,
		color: '#5f6e8f',
		lineHeight: 1.4,
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden',
		wordBreak: 'break-word'
	},
	time: {
		margin: 0,
		marginTop: 6,
		fontSize: 11,
		color: '#9aa3b2',
		lineHeight: 1.3
	},
	closeBtn: {
		padding: 4,
		marginTop: -2,
		marginRight: -4,
		flexShrink: 0
	}
}));

function getNotificationKey(item) {
	if (!item || typeof item !== 'object') {
		return null;
	}
	return item.notificationId ?? item.id ?? null;
}

function isImportantNotification(notification) {
	return (
		notification?.important === true ||
		notification?.isImportant === true ||
		String(notification?.important).toLowerCase() === 'true' ||
		String(notification?.isImportant).toLowerCase() === 'true'
	);
}

function isEligibleForToast(notification, sessionStartMs) {
	if (!notification) {
		return false;
	}
	const createdAt = notification.createdAt;
	if (!createdAt) {
		return true;
	}
	const createdMs = new Date(createdAt).getTime();
	if (Number.isNaN(createdMs)) {
		return true;
	}
	if (createdMs < sessionStartMs) {
		return false;
	}
	const createdDate = new Date(createdAt);
	const now = new Date();
	return createdDate.toDateString() === now.toDateString();
}

const NOTIFICATION_CREATED_AT_FORMATS = ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601];

function parseNotificationCreatedAt(iso) {
	return moment(iso, NOTIFICATION_CREATED_AT_FORMATS, true);
}

function formatWhen(iso) {
	if (!iso) {
		return '';
	}
	const m = parseNotificationCreatedAt(iso);
	if (!m.isValid()) {
		const fallback = moment(iso);
		return fallback.isValid() ? fallback.fromNow() : String(iso);
	}
	return m.fromNow();
}

/* Added the following code for POW-946.Harsha using AI Agent. September 28, 2026. Code Start */
function isMissingSubmissionJobReminder(notification) {
	const subject = String(notification?.subject || '');
	return subject.indexOf('No submission for assigned job (Req ') === 0;
}

function parseNotificationContentWithLink(content) {
	const text = String(content || '').trim();
	const urlMatch = text.match(/https?:\/\/[^\s]+/);
	if (!urlMatch) {
		return { body: text, url: '' };
	}
	const url = urlMatch[0];
	const body = text.replace(url, '').trim();
	return { body, url };
}

function navigateToJobLink(history, rawUrl) {
	if (!rawUrl || !history) {
		return;
	}
	try {
		const parsed = new URL(rawUrl, window.location.origin);
		history.push(`${parsed.pathname}${parsed.search}${parsed.hash}`);
	} catch (e) {
		window.location.href = rawUrl;
	}
}
/* Code end. Harsha using AI Agent. September 28, 2026 */

function NotificationToastStack() {
	const classes = useStyles();
	const history = useHistory();
	const { notifications, initialLoadComplete } = useNotificationsContext();
	const [visibleToasts, setVisibleToasts] = useState([]);
	const sessionStartMsRef = useRef(Date.now());
	const knownIdsRef = useRef(new Set());
	const toastedIdsRef = useRef(new Set());
	const pendingWhileHiddenRef = useRef([]);
	const initialSeedDoneRef = useRef(false);

	const dismissToast = useCallback(notificationKey => {
		setVisibleToasts(prev => prev.filter(item => String(getNotificationKey(item)) !== String(notificationKey)));
	}, []);

	const showToast = useCallback(notification => {
		const key = getNotificationKey(notification);
		if (key === undefined || key === null) {
			return;
		}
		const keyStr = String(key);
		if (toastedIdsRef.current.has(keyStr)) {
			return;
		}
		toastedIdsRef.current.add(keyStr);
		setVisibleToasts(prev => {
			const exists = prev.some(item => String(getNotificationKey(item)) === keyStr);
			if (exists) {
				return prev;
			}
			const next = [...prev, notification];
			if (next.length <= MAX_VISIBLE_WHILE_ACTIVE) {
				return next;
			}
			return next.slice(next.length - MAX_VISIBLE_WHILE_ACTIVE);
		});
	}, []);

	const enqueueNotification = useCallback(
		notification => {
			if (!isEligibleForToast(notification, sessionStartMsRef.current)) {
				return;
			}
			if (typeof document !== 'undefined' && document.hidden) {
				pendingWhileHiddenRef.current.push(notification);
				return;
			}
			showToast(notification);
		},
		[showToast]
	);

	const flushHiddenQueue = useCallback(() => {
		const pending = pendingWhileHiddenRef.current;
		pendingWhileHiddenRef.current = [];
		if (!pending.length) {
			return;
		}
		const latest = pending.slice(-MAX_VISIBLE_AFTER_TAB_RETURN);
		latest.forEach(notification => {
			showToast(notification);
		});
	}, [showToast]);

	useEffect(() => {
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				flushHiddenQueue();
			}
		};
		document.addEventListener('visibilitychange', onVisibilityChange);
		return () => document.removeEventListener('visibilitychange', onVisibilityChange);
	}, [flushHiddenQueue]);

	useEffect(() => {
		if (!initialLoadComplete || initialSeedDoneRef.current) {
			return;
		}
		notifications.forEach(item => {
			const key = getNotificationKey(item);
			if (key !== undefined && key !== null) {
				knownIdsRef.current.add(String(key));
			}
		});
		initialSeedDoneRef.current = true;
	}, [notifications, initialLoadComplete]);

	useEffect(() => {
		if (!initialLoadComplete || !initialSeedDoneRef.current) {
			return;
		}
		notifications.forEach(item => {
			const key = getNotificationKey(item);
			if (key === undefined || key === null) {
				return;
			}
			const keyStr = String(key);
			if (knownIdsRef.current.has(keyStr)) {
				return;
			}
			knownIdsRef.current.add(keyStr);
			enqueueNotification(item);
		});
	}, [notifications, initialLoadComplete, enqueueNotification]);

	if (!visibleToasts.length) {
		return null;
	}

	return (
		<div className={classes.root} aria-live="polite" aria-label="Notification popups">
			{visibleToasts.map(notification => {
				const key = getNotificationKey(notification);
				const important = isImportantNotification(notification);
				const preview = String(notification.content || '').trim();
				/* Added the following code for POW-946.Harsha using AI Agent. September 28, 2026. Code Start */
				const isPow946Reminder = isMissingSubmissionJobReminder(notification);
				const { body: previewBody, url: jobLinkUrl } = parseNotificationContentWithLink(preview);
				/* Code end. Harsha using AI Agent. September 28, 2026 */
				return (
					<Paper
						key={String(key)}
						className={`${classes.toast} ${important ? classes.toastImportant : ''}`}
						elevation={0}
					>
						<div className={classes.body}>
							<Typography
								component="div"
								className={`${classes.title} ${important ? classes.titleImportant : ''}`}
							>
								{notification.subject || 'Notification'}
							</Typography>
							{preview ? (
								<Typography component="div" className={classes.preview}>
									{isPow946Reminder && previewBody ? previewBody : preview}
								</Typography>
							) : null}
							{/* Added the following code for POW-946.Harsha using AI Agent. September 28, 2026. Code Start */}
							{isPow946Reminder && jobLinkUrl ? (
								<Typography component="div" className={classes.preview} style={{ marginTop: 6 }}>
									<button
										type="button"
										style={{
											background: 'none',
											border: 'none',
											padding: 0,
											color: accentBlue,
											cursor: 'pointer',
											textDecoration: 'underline',
											fontSize: 12,
											textAlign: 'left',
											wordBreak: 'break-all'
										}}
										onClick={() => navigateToJobLink(history, jobLinkUrl)}
									>
										{jobLinkUrl}
									</button>
								</Typography>
							) : null}
							{/* Code end. Harsha using AI Agent. September 28, 2026 */}
							{!isPow946Reminder ? (
								<Typography component="div" className={classes.time}>
									{formatWhen(notification.createdAt)}
								</Typography>
							) : null}
						</div>
						<IconButton
							size="small"
							className={classes.closeBtn}
							aria-label="Close notification popup"
							onClick={() => dismissToast(key)}
						>
							<X size={16} color="#6f7b96" strokeWidth={2} />
						</IconButton>
					</Paper>
				);
			})}
		</div>
	);
}

export default NotificationToastStack;
/* Code end. Harsha using AI Agent. September 20, 2026 */
